import React from "react";

/**
 * Celsius brand mark — sourced directly from the client letterhead (`Celsius LH.docx`).
 *
 * Variants:
 *   - "mark"  → just the concentric-C mark with the dot accent (icon-only lockup, tight nav)
 *   - "full"  → mark + "celsius" wordmark (login, invoices, feedback)
 *
 * Schemes:
 *   - "light" (default) → dark slate colour on white/light backgrounds
 *   - "dark"            → white on dark backgrounds (shell bar, feedback header)
 *
 * Assets live in /public/brand as pre-tinted transparent PNGs.
 */
export default function CelsiusLogo({
  variant = "full",
  scheme = "light",
  size = 32,
  className = "",
  showTagline = false,
  tagline = "",
  ...props
}) {
  const isDark = scheme === "dark";
  const markSrc = isDark ? "/brand/celsius-mark-white.png" : "/brand/celsius-mark.png";
  const fullSrc = isDark ? "/brand/celsius-white.png" : "/brand/celsius-dark.png";

  if (variant === "mark") {
    return (
      <img
        {...props}
        src={markSrc}
        alt="Celsius"
        style={{ height: size, width: "auto", ...(props.style || {}) }}
        className={className}
      />
    );
  }

  // Full lockup (mark + wordmark from the letterhead itself)
  const height = size * 1.6; // wordmark image is roughly 1:1.6 (H:W)
  return (
    <span className={`inline-flex flex-col items-start ${className}`} aria-label="Celsius">
      <img
        src={fullSrc}
        alt="Celsius"
        style={{ height, width: "auto" }}
      />
      {showTagline && (
        <span
          className="mt-1 uppercase tracking-[0.3em]"
          style={{
            color: isDark ? "#94A3B8" : "#64748B",
            fontSize: `${size * 0.28}px`,
            letterSpacing: "0.3em",
          }}
        >
          {tagline}
        </span>
      )}
    </span>
  );
}
